/**
 * Auth Layout Component
 * Desktop split-screen layout with form on right
 * Mobile shows video at top, form below
 * Includes automatic iOS-style page transitions
 * 
 * @author Varun Pandey
 */

import React, { useRef, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import profileWave from '../../assets/images/profile-wave.png';
import { useVideoStore } from '../../stores/videoStore';
import { Vortex } from '@/components/ui/vortex';
import { StarsBackground } from '@/components/ui/stars-background';
import { ShootingStars } from '@/components/ui/shooting-stars';
import { Spotlight } from '../ui/SpotlightNew';

interface AuthLayoutProps {
  children: React.ReactNode;
  showImage?: boolean;
  fullWidth?: boolean;
  /** Unique key for page transitions - changes trigger animation */
  transitionKey?: string | number;
  /** Disable transitions for this instance */
  disableTransition?: boolean;
  /** Background style: 'vortex' (default), 'spotlight', or 'stars' */
  backgroundVariant?: 'vortex' | 'spotlight' | 'stars';
  /** Fill parent height instead of min-h-screen — for nested signup steps with progress bar */
  constrainedHeight?: boolean;
}

// iOS-optimized easing curves (typed as tuples for Framer Motion)
const iosEasing: [number, number, number, number] = [0.25, 0.1, 0.25, 1.0];
const iosExitEasing: [number, number, number, number] = [0.4, 0.0, 1, 1];

// Video asset path served from the public folder (e.g. public/videos/cockpit-landing.webm)
const cockpitLandingVideo = '/videos/cockpit-landing.webm';

// Content animation variants
const contentVariants = {
  initial: {
    opacity: 0,
    y: 12,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: iosEasing,
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: {
      duration: 0.25,
      ease: iosExitEasing,
    },
  },
};

export const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  showImage = true,
  fullWidth = false,
  transitionKey,
  disableTransition = false,
  backgroundVariant = 'vortex',
  constrainedHeight = false,
}) => {
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const desktopVideoRef = useRef<HTMLVideoElement>(null);
  const showVideoFromStore = useVideoStore((state) => state.showVideo);
  const isNativeApp = Capacitor.isNativePlatform();
  const shouldShowDesktopVideo = showVideoFromStore && !isNativeApp;

  const shouldShowMobileMedia = showImage && showVideoFromStore;
  const [isWebmSupported, setIsWebmSupported] = useState(true);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const v = document.createElement('video');
    const canPlay = v.canPlayType?.('video/webm; codecs="vp8, vorbis"') || v.canPlayType?.('video/webm') || '';
    setIsWebmSupported(Boolean(canPlay));
  }, []);

  const showMobileVideo = useMemo(
    () => Boolean(shouldShowMobileMedia && isWebmSupported),
    [shouldShowMobileMedia, isWebmSupported],
  );
  const showMobileFallbackImage = useMemo(
    () => Boolean(shouldShowMobileMedia && !isWebmSupported),
    [shouldShowMobileMedia, isWebmSupported],
  );

  useEffect(() => {
    const video = mobileVideoRef.current;
    if (!video || !showMobileVideo) return;

    const playVideo = async () => {
      try {
        if (video.paused || video.ended) {
          video.currentTime = 0;
          await video.play();
        }
      } catch (error) {
        // Silently handle autoplay restrictions
      }
    };

    playVideo();

    const handleEnded = () => {
      video.currentTime = 0;
      video.play().catch(() => { });
    };

    const handlePause = () => {
      if (!video.ended) {
        video.play().catch(() => { });
      }
    };

    video.addEventListener('ended', handleEnded, { passive: true });
    video.addEventListener('pause', handlePause, { passive: true });

    return () => {
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('pause', handlePause);
    };
  }, [showMobileVideo]);

  // Initialize desktop video playback
  useEffect(() => {
    const video = desktopVideoRef.current;
    if (!video || !showImage || !shouldShowDesktopVideo) return;

    const playVideo = async () => {
      try {
        if (video.paused || video.ended) {
          video.currentTime = 0;
          await video.play();
        }
      } catch (error) {
        // Silently handle autoplay restrictions
      }
    };

    playVideo();

    const handleEnded = () => {
      video.currentTime = 0;
      video.play().catch(() => { });
    };

    const handlePause = () => {
      if (shouldShowDesktopVideo && !video.ended) {
        video.play().catch(() => { });
      }
    };

    video.addEventListener('ended', handleEnded, { passive: true });
    video.addEventListener('pause', handlePause, { passive: true });

    return () => {
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('pause', handlePause);
    };
  }, [showImage, shouldShowDesktopVideo]);

  const isDesktopVideoVisible = showImage && shouldShowDesktopVideo;

  const scrollContainerClasses = constrainedHeight
    ? 'flex-1 min-h-0 h-full overflow-hidden'
        + (isDesktopVideoVisible
          ? (fullWidth ? ' lg:w-[55%] lg:px-8 lg:py-8' : ' items-center justify-start lg:justify-center lg:min-h-screen lg:pt-0 lg:w-[55%] lg:px-12 lg:py-8 lg:pb-8')
          : (fullWidth ? ' lg:w-full lg:px-8 lg:py-8' : ' items-center justify-start lg:justify-center lg:min-h-screen lg:pt-0 lg:w-full lg:px-12 lg:py-8 lg:pb-8'))
    : (isDesktopVideoVisible
      ? (fullWidth
        ? 'lg:w-[55%] lg:px-8 lg:py-8'
        : 'items-center justify-start min-h-[var(--app-height)] pb-4 pt-4 lg:justify-center lg:min-h-screen lg:pt-0 lg:w-[55%] lg:px-12 lg:py-8 lg:pb-8')
      : (fullWidth
        ? 'lg:w-full lg:px-8 lg:py-8'
        : 'items-center justify-start min-h-[var(--app-height)] pb-4 pt-4 lg:justify-center lg:min-h-screen lg:pt-0 lg:w-full lg:px-12 lg:py-8 lg:pb-8'));

  const contentWrapperClass = fullWidth ? 'w-full' : 'w-full max-w-md';
  const contentWrapperHeightClass = constrainedHeight ? ' h-full min-h-0 flex flex-col' : '';

  const layoutContent = (
    <>
      {/* Shared subtle dim layer above any background */}
      <div className="pointer-events-none absolute inset-0 z-0 bg-black/40" />
      {/* Mobile video - shows at top on mobile web, hidden on desktop */}

      {showMobileFallbackImage && (
        <div className="w-full relative lg:hidden z-0 pointer-events-none" style={{ marginBottom: '0' }}>
          <div className="w-full overflow-hidden rounded-br-[5rem]">
            <div
              className="video-rounded-container"
              style={{
                width: '100%',
                height: 'auto',
                overflow: 'hidden',
                position: 'relative',
                isolation: 'isolate',
                transform: 'translateZ(0)',
                WebkitTransform: 'translateZ(0)',
                borderBottomRightRadius: '0.75rem',
                WebkitBorderBottomRightRadius: '0.75rem',
              }}
            >
              <img
                src={profileWave}
                alt=""
                className="w-full h-auto block"
                style={{
                  objectFit: 'cover',
                  objectPosition: 'center',
                  transform: 'translateZ(0)',
                  WebkitTransform: 'translateZ(0)',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                  borderBottomRightRadius: '0.75rem',
                  WebkitBorderBottomRightRadius: '0.75rem',
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Left side - Video for signup, empty for signin on desktop */}
      {/* Always show left section on desktop to ensure form stays on right */}
      {/* Hidden on mobile - only desktop video shows here */}
      <style>{`
        @media (max-width: 1023px) {
          .desktop-video-left-section {
            display: none !important;
          }
        }
      `}</style>
      <div className={`desktop-video-left-section hidden lg:flex lg:items-center lg:justify-center lg:w-[45%] ${!isDesktopVideoVisible ? 'lg:hidden' : ''}`}>
        {isDesktopVideoVisible && (
          <div
            className="rounded-[0.75rem] overflow-hidden"
            style={{
              width: 'fit-content',
              maxWidth: '100%',
              paddingLeft: '6rem',
              paddingRight: '2rem',
              paddingBottom: '4rem',
            }}
          >
            <div
              className="video-rounded-container"
              style={{
                width: 'fit-content',
                height: 'fit-content',
                borderRadius: '4rem',
                WebkitBorderRadius: '0.75rem',
                overflow: 'hidden',
                position: 'relative',
                isolation: 'isolate',
                transform: 'translateZ(0)',
                WebkitTransform: 'translateZ(0)',
              }}
            >
              <video
                ref={desktopVideoRef}
                className="video-element-rounded"
                style={{
                  objectFit: 'contain',
                  objectPosition: 'center',
                  display: 'block',
                  transform: 'translateZ(0)',
                  WebkitTransform: 'translateZ(0)',
                  willChange: 'auto',
                  pointerEvents: 'none',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                }}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
              >
                <source src={cockpitLandingVideo} type="video/webm" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        )}
      </div>

      {/* Right side - Form content with automatic page transitions */}
      <div
        data-auth-scroll-container="true"
        className={`auth-scroll-container flex w-full flex-col overflow-x-hidden lg:overflow-visible ${constrainedHeight ? 'overflow-hidden' : 'overflow-y-auto'} ${scrollContainerClasses}`}
        style={{ alignSelf: constrainedHeight ? 'stretch' : 'flex-start' }}
      >
        {disableTransition ? (
          <div className={`${contentWrapperClass}${contentWrapperHeightClass}`}>{children}</div>
        ) : (
          <motion.div
            key={transitionKey}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={contentVariants}
            className={`${contentWrapperClass}${contentWrapperHeightClass}`}
            style={{
              // GPU acceleration for smooth iOS animations
              willChange: 'opacity, transform',
              WebkitBackfaceVisibility: 'hidden',
              backfaceVisibility: 'hidden',
              transform: 'translateZ(0)',
              WebkitTransform: 'translateZ(0)',
            }}
          >
            {children}
          </motion.div>
        )}
      </div>
    </>
  );

  if (backgroundVariant === 'spotlight') {
    return (
      <div
        className={`flex flex-col lg:flex-row bg-[#0B0B0D] relative overflow-hidden ${constrainedHeight ? 'h-full min-h-0' : 'lg:min-h-screen'}`}
        style={constrainedHeight ? undefined : { minHeight: 'var(--app-height, 100dvh)' }}
      >
        <Spotlight className="-z-10" />
        {layoutContent}
      </div>
    );
  }

  if (backgroundVariant === 'stars') {
    return (
      <div
        className={`bg-black relative overflow-hidden flex flex-col lg:flex-row ${constrainedHeight ? 'h-full min-h-0' : 'min-h-screen'}`}
        style={constrainedHeight ? undefined : { minHeight: 'var(--app-height, 100dvh)' }}
      >
        <StarsBackground className="-z-10" />
        <ShootingStars className="-z-10" />
        {layoutContent}
      </div>
    );
  }

  return (
    <div
      className={`bg-black ${constrainedHeight ? 'h-full min-h-0' : 'min-h-screen'}`}
      style={constrainedHeight ? undefined : { minHeight: 'var(--app-height, 100dvh)' }}
    >
      <Vortex
        backgroundColor="#000000"
        containerClassName="flex flex-col lg:flex-row bg-transparent lg:min-h-screen relative overflow-hidden"
        className="flex flex-col lg:flex-row w-full"
      >
        {layoutContent}
      </Vortex>
    </div>
  );
};
